import { makeRequest, apiClient } from '../../../../services/apiClient';

const isNotFound = (err) => {
  if (!err) return false;
  const status = err?.response?.status;
  if (status === 404) return true;
  const msg = String(err?.message || err || '');
  return /Not Found/i.test(msg) || /status code\s*404/i.test(msg) || /\b404\b/.test(msg);
};

const getBackendOrigin = () => {
  try {
    const base = apiClient?.defaults?.baseURL || '';
    if (!base) return null;
    const u = new URL(base, window?.location?.origin);
    return u.origin;
  } catch (e) {
    return null;
  }
};

/**
 * Fetch KPI for events that do not reference any objects (ocel:omap missing/empty/invalid).
 * Returns MissingEventObjectRefsResponse structure. Falls back to simple endpoints and computes ratio with total events.
 */
export const fetchMissingEventObjectRefsKPI = async (fileType = null) => {
  const params = fileType ? { file_type: fileType } : {};

  // Try primary endpoints - KPI router is mounted at /api/kpi
  try {
    return await makeRequest('/kpi/events-without-objects', { params }, 'events without objects KPI');
  } catch (e) {
    if (!isNotFound(e)) throw e;
  }
  try {
    return await makeRequest('/events-without-objects', { params }, 'events without objects KPI (legacy)');
  } catch (e) {
    if (!isNotFound(e)) throw e;
  }

  // Simple endpoints
  let simple;
  try {
    simple = await makeRequest('/events-without-objects-simple', { params }, 'events without objects KPI (simple)');
  } catch (e3) {
    if (!isNotFound(e3)) throw e3;
    try {
      simple = await makeRequest('/analytics/events-without-objects-simple', { params }, 'events without objects KPI (simple alias)');
    } catch (e4) {
      if (!isNotFound(e4)) throw e4;

      if (typeof window !== 'undefined' && window.location) {
        const origin = getBackendOrigin() || window.location.origin;
        const candidates = [
          // Primary
          `${origin}/events-without-objects`,
          `${origin}/api/events-without-objects`,
          `${origin}/api/v1/events-without-objects`,
          `${origin}/v1/events-without-objects`,
          `${origin}/analytics/events-without-objects`,
          `${origin}/api/analytics/events-without-objects`,
          `${origin}/api/v1/analytics/events-without-objects`,
          `${origin}/v1/analytics/events-without-objects`,
          // Simple
          `${origin}/events-without-objects-simple`,
          `${origin}/api/events-without-objects-simple`,
          `${origin}/api/v1/events-without-objects-simple`,
          `${origin}/v1/events-without-objects-simple`,
          `${origin}/analytics/events-without-objects-simple`,
          `${origin}/api/analytics/events-without-objects-simple`,
          `${origin}/api/v1/analytics/events-without-objects-simple`,
          `${origin}/v1/analytics/events-without-objects-simple`,
          // Gateway variants
          `${origin}/gateway/api/events-without-objects`,
          `${origin}/gateway/api/analytics/events-without-objects`,
          `${origin}/gateway/api/events-without-objects-simple`,
          `${origin}/gateway/api/analytics/events-without-objects-simple`,
        ];
        for (const url of candidates) {
          try {
            if (/events-without-objects(?!-simple)/.test(url)) {
              const res = await makeRequest(url, { params }, `events without objects KPI (${url})`);
              if (res && typeof res === 'object' && 'total_events' in res) return res;
            }
          } catch (e5) {
            if (!isNotFound(e5)) throw e5;
          }
        }
        for (const url of candidates) {
          if (/events-without-objects-simple/.test(url)) {
            try {
              simple = await makeRequest(url, { params }, `events without objects KPI (simple ${url})`);
              if (simple) break;
            } catch (e6) {
              if (!isNotFound(e6)) throw e6;
            }
          }
        }
      }
    }
  }

  const invalidCount = typeof simple?.events_without_objects === 'number' ? simple.events_without_objects : 0;

  // Fetch total events to compute ratio
  let total = 0;
  try {
    const totalResp = await makeRequest('/total-events', { params }, 'total events for events without objects');
    total = typeof totalResp?.totalEvents === 'number' ? totalResp.totalEvents : 0;
  } catch (te) {
    if (!isNotFound(te)) throw te;
    try {
      const totalResp2 = await makeRequest('/ocel/total-events', { params }, 'total events for events without objects (alias)');
      total = typeof totalResp2?.totalEvents === 'number' ? totalResp2.totalEvents : 0;
    } catch (te2) {
      if (!isNotFound(te2)) throw te2;
      if (typeof window !== 'undefined' && window.location) {
        const origin = getBackendOrigin() || window.location.origin;
        const totalCandidates = [
          `${origin}/total-events`,
          `${origin}/api/total-events`,
          `${origin}/api/v1/total-events`,
          `${origin}/v1/total-events`,
          `${origin}/ocel/total-events`,
          `${origin}/api/ocel/total-events`,
          `${origin}/gateway/api/total-events`,
          `${origin}/gateway/api/ocel/total-events`,
        ];
        for (const url of totalCandidates) {
          try {
            const totalRespX = await makeRequest(url, { params }, `total events for events without objects (${url})`);
            total = typeof totalRespX?.totalEvents === 'number' ? totalRespX.totalEvents : 0;
            if (total > 0) break;
          } catch (e7) {
            if (!isNotFound(e7)) throw e7;
          }
        }
      }
    }
  }

  const ratio = total > 0 ? invalidCount / total : 0;
  return {
    file_type: fileType ?? 'main',
    total_events: total,
    events_without_objects: invalidCount,
    missing_ratio: ratio,
    considered_keys: ['ocel:omap', 'omap']
  };
};